import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Paper, Stack,
  CircularProgress, Alert, MenuItem, alpha,
} from "@mui/material";
import {
  School, CheckCircle, Email, Lock, Person,
  LocationOn, Cake, Phone,
} from "@mui/icons-material";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

const API_BASE = "https://backend-global-1vq9.onrender.com/api/auth";

const getMaxDob = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [validateError, setValidateError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [location, setLocation] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [gender, setGender] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const maxDob = useMemo(() => getMaxDob(), []);

  // ── Validate token + pre-fill ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/invite/check/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.msg || "Invalid invitation");
        setEmail(data.email);
        if (data.fullName) setFullName(data.fullName);
      } catch (err) {
        setValidateError(err.message);
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (password !== confirmPassword) { setSubmitError("Passwords do not match"); return; }
    if (password.length < 8) { setSubmitError("Password must be at least 8 characters"); return; }
    if (!fullName.trim() || !location.trim() || !dateOfBirth) {
      setSubmitError("Please fill in name, location, and date of birth");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE}/invite/accept/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName, location, dateOfBirth,
          phoneNumber: phoneNumber || undefined,
          gender: gender || undefined,
          password, confirmPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Failed to accept invite");

      // Log them in immediately
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      navigate("/mentor-dash", {
        state: { notice: "Welcome aboard! Your mentor account is now active." },
      });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Validating state ─────────────────────────────────────────────────────
  if (validating) {
    return (
      <Box sx={{
        minHeight: "100vh", bgcolor: C.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress sx={{ color: C.primary }} />
          <Typography sx={{ color: C.textSecondary, fontWeight: 600 }}>
            Checking your invitation…
          </Typography>
        </Stack>
      </Box>
    );
  }

  // ── Invalid / expired invite ─────────────────────────────────────────────
  if (validateError) {
    return (
      <Box sx={{
        minHeight: "100vh", bgcolor: C.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        p: 2,
      }}>
        <Paper elevation={0} sx={{
          maxWidth: 480, p: 4, borderRadius: 3,
          border: `1px solid ${C.border}`,
          textAlign: "center",
        }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: "50%",
            bgcolor: C.errorSoft, color: C.error,
            display: "flex", alignItems: "center", justifyContent: "center",
            mx: "auto", mb: 2,
          }}>
            <Email sx={{ fontSize: 32 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 22, color: C.textPrimary, mb: 1 }}>
            Invitation Unavailable
          </Typography>
          <Typography sx={{ color: C.textSecondary, fontSize: 14, lineHeight: 1.6, mb: 3 }}>
            {validateError} Please contact your administrator to receive a new invitation.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{
              bgcolor: C.primary, color: "#fff",
              borderRadius: 2.5, py: 1.25, px: 3,
              fontWeight: 700, textTransform: "none",
              "&:hover": { bgcolor: C.primaryDark },
            }}
          >
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  // ── Acceptance form ──────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: C.bg, display: "flex", flexDirection: "column" }}>
      {/* Hero */}
      <Box sx={{
        position: "relative", overflow: "hidden",
        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
        color: "#fff",
        px: { xs: 3, sm: 5 },
        py: { xs: 5, sm: 7 },
        textAlign: "center",
      }}>
        <Box sx={{
          position: "absolute", top: "-40%", right: "-10%",
          width: 300, height: 300, borderRadius: "50%",
          background: `radial-gradient(circle, ${C.secondary}50 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.18)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            mb: 2,
          }}>
            <School sx={{ fontSize: 36, color: "#fff" }} />
          </Box>
          <Typography sx={{
            fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
            textTransform: "uppercase", color: "rgba(255,255,255,0.75)", mb: 1,
          }}>
            Mentor Invitation
          </Typography>
          <Typography sx={{
            fontWeight: 800, fontSize: { xs: 26, sm: 34 },
            color: "#fff", lineHeight: 1.15, mb: 1.5, letterSpacing: -0.5,
          }}>
            Welcome to Global Impact
          </Typography>
          <Typography sx={{
            color: "rgba(255,255,255,0.85)",
            fontSize: { xs: 14, sm: 15 },
            maxWidth: 520, mx: "auto", lineHeight: 1.5,
          }}>
            Complete your account below to start mentoring learners on the platform
          </Typography>
        </Box>
      </Box>

      {/* Form */}
      <Box sx={{
        flex: 1, display: "flex", justifyContent: "center",
        px: 2, py: { xs: 3, sm: 5 },
      }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%", maxWidth: 540,
            p: { xs: 2.5, sm: 4 }, borderRadius: 3,
            border: `1px solid ${C.border}`,
            bgcolor: C.paper,
            boxShadow: "0 4px 24px rgba(13,17,23,0.06)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "3px",
              background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`,
            },
          }}
        >
          {/* Pre-filled email banner */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            bgcolor: C.primarySofter,
            border: `1px solid ${C.primarySoft}`,
            borderRadius: 2.5, p: 1.5, mb: 3,
          }}>
            <Email sx={{ fontSize: 18, color: C.primary }} />
            <Box minWidth={0}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Invited Email
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, wordBreak: "break-all" }}>
                {email}
              </Typography>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormField icon={<Person sx={{ fontSize: 16 }} />} label="Full Name *">
                <TextField
                  fullWidth size="small" placeholder="Your full name"
                  value={fullName} onChange={(e) => setFullName(e.target.value)}
                  required sx={fieldSx}
                />
              </FormField>

              <FormField icon={<LocationOn sx={{ fontSize: 16 }} />} label="Location *">
                <TextField
                  fullWidth size="small" placeholder="City, Country"
                  value={location} onChange={(e) => setLocation(e.target.value)}
                  required sx={fieldSx}
                />
              </FormField>

              <FormField icon={<Cake sx={{ fontSize: 16 }} />} label="Date of Birth * (must be 18+)">
                <TextField
                  fullWidth size="small" type="date"
                  value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)}
                  inputProps={{ max: maxDob }}
                  required sx={fieldSx}
                />
              </FormField>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Box flex={1}>
                  <FormField icon={<Phone sx={{ fontSize: 16 }} />} label="Phone (optional)">
                    <TextField
                      fullWidth size="small" placeholder="+254..."
                      value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                      sx={fieldSx}
                    />
                  </FormField>
                </Box>
                <Box flex={1}>
                  <FormField icon={<Person sx={{ fontSize: 16 }} />} label="Gender (optional)">
                    <TextField
                      fullWidth size="small" select
                      value={gender} onChange={(e) => setGender(e.target.value)}
                      sx={fieldSx}
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  </FormField>
                </Box>
              </Stack>

              <FormField icon={<Lock sx={{ fontSize: 16 }} />} label="Password * (min 8 chars)">
                <TextField
                  fullWidth size="small" type={showPassword ? "text" : "password"}
                  placeholder="Choose a strong password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  required sx={fieldSx}
                />
              </FormField>

              <FormField icon={<Lock sx={{ fontSize: 16 }} />} label="Confirm Password *">
                <TextField
                  fullWidth size="small" type={showPassword ? "text" : "password"}
                  placeholder="Re-enter password"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  required sx={fieldSx}
                />
              </FormField>

              <Box sx={{ textAlign: "right" }}>
                <Typography
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{
                    fontSize: 12, color: C.primary, fontWeight: 700,
                    cursor: "pointer", display: "inline-block",
                  }}
                >
                  {showPassword ? "Hide" : "Show"} Password
                </Typography>
              </Box>

              {submitError && (
                <Alert severity="error" sx={{ borderRadius: 2.5, fontSize: 13, fontWeight: 600 }}>
                  {submitError}
                </Alert>
              )}

              <Button
                type="submit" fullWidth disabled={submitting}
                startIcon={!submitting && <CheckCircle sx={{ fontSize: 18 }} />}
                sx={{
                  background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryDark} 100%)`,
                  color: "#fff",
                  borderRadius: 2.5,
                  py: 1.5, fontSize: 14, fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: `0 4px 14px ${C.secondary}40`,
                  },
                  "&.Mui-disabled": { background: "#CCC", color: "#fff" },
                }}
              >
                {submitting ? "Activating account…" : "Activate My Account"}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Box>
    </Box>
  );
}

// Small wrapper that pairs an icon + label above a form field
function FormField({ icon, label, children }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
        <Box sx={{ color: C.textMuted, display: "flex" }}>{icon}</Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.textSecondary, letterSpacing: 0.2 }}>
          {label}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    fontSize: 14,
    "&:hover fieldset": { borderColor: C.primary },
    "&.Mui-focused fieldset": { borderColor: C.primary },
  },
};